import { useState, useCallback, useRef, useEffect, useContext } from 'react';
import dataSyncApi from '../../../../api/dataSync/api';
import appSettingsApi from '../../../../api/appSettings/api';
import { TableConfig, LogLine, TableStatus, SyncJobStatus, S3Snapshot, JobProgress, SyncTarget } from '../../../../api/dataSync/types';
import { PagingConfig } from '../../../../api/appSettings/types';
import { Context as AppSettingsContext } from '../../../../api/appSettings/context';
import AuthContext from '../../../../api/auth/context/context';

const TABLES = [
    'passengers',
    'hotels',
    'exchangeRates',
    'suppliers',
    'tours',
    'travelfiles',
    'documents',
    'bspdata',
    'reservationHistory'
];

// Default to the non-destructive rule. 'override' maps to --clear, which deletes every row in the
// tables it touches — including rows created in the app, which have no Galor source and cannot be
// re-imported. Defaulting a page that targets production to a full destructive rebuild puts one
// click between an operator and unrecoverable data loss. A full rebuild stays available, but has
// to be chosen deliberately.
const initialTableConfigs: TableConfig[] = TABLES.map((name) => ({
    name,
    enabled: true,
    importRule: 'incremental' as const
}));

const LOG_COLOR_MAP: Record<string, string> = {
    error: '#f44747',
    progress: '#6a9955',
    info: '#c586c0'
};

export default function useDataSync() {
    const { isLoggedIn } = useContext(AuthContext);
    const { settings, update: updateSetting } = useContext(AppSettingsContext);

    const [tableConfigs, setTableConfigs] = useState<TableConfig[]>(initialTableConfigs);
    const [targetDb, setTargetDb] = useState<SyncTarget>('local');
    const [limit, setLimit] = useState<string>('');
    const [importFromRedshift, setImportFromRedshift] = useState(false);
    const [redshiftMode, setRedshiftMode] = useState<'partial' | 'full'>('partial');

    // Galor source DB and redshift target DB — init from context settings
    const [galorSourceDb, setGalorSourceDb] = useState<string>('galor_db');
    const [redshiftTargetDb, setRedshiftTargetDb] = useState<string>('galor_db');
    const [localDatabases, setLocalDatabases] = useState<string[]>([]);

    // Paging config — local copy for editing, synced back to context on save
    const [pagingConfig, setPagingConfig] = useState<PagingConfig | null>(null);
    const [pagingDirty, setPagingDirty] = useState(false);

    // S3 Snapshots
    const [snapshots, setSnapshots] = useState<S3Snapshot[]>([]);
    const [snapshotsLoading, setSnapshotsLoading] = useState(false);

    const [jobId, setJobId] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<SyncJobStatus>('idle');
    const [tableStatuses, setTableStatuses] = useState<Record<string, TableStatus>>({});
    const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);

    // Terminal: direct DOM ref instead of React state to avoid re-renders
    const terminalRef = useRef<HTMLDivElement | null>(null);
    const logCountRef = useRef(0);
    const logCacheRef = useRef<LogLine[]>([]);

    const eventSourceRef = useRef<EventSource | null>(null);
    const jobIdRef = useRef<string | null>(null);

    // Render a single log line into a container DOM element
    const renderLogLine = useCallback((container: HTMLElement, log: LogLine) => {
        const line = document.createElement('div');
        line.style.color = LOG_COLOR_MAP[log.type] || LOG_COLOR_MAP.info;

        const ts = document.createElement('span');
        ts.style.color = '#808080';
        ts.textContent = log.timestamp;
        line.appendChild(ts);

        if (log.table) {
            const tbl = document.createElement('span');
            tbl.style.color = '#569cd6';
            tbl.textContent = ` [${log.table}]`;
            line.appendChild(tbl);
        }

        line.appendChild(document.createTextNode(` ${log.text}`));
        container.appendChild(line);
    }, []);

    // Append a log line directly to the terminal DOM (no React state)
    const appendLog = useCallback((log: LogLine) => {
        logCacheRef.current.push(log);
        // Keep max 2000 in memory
        if (logCacheRef.current.length > 2000) {
            logCacheRef.current.shift();
        }

        const container = terminalRef.current;
        if (!container) return;

        renderLogLine(container, log);
        logCountRef.current++;

        // Auto-scroll to bottom
        container.scrollTop = container.scrollHeight;
    }, [renderLogLine]);

    // Ref callback: when terminal DOM mounts, replay cached logs
    const setTerminalRef = useCallback((node: HTMLDivElement | null) => {
        terminalRef.current = node;
        if (node && logCacheRef.current.length > 0) {
            node.innerHTML = '';
            for (const log of logCacheRef.current) {
                renderLogLine(node, log);
            }
            logCountRef.current = logCacheRef.current.length;
            node.scrollTop = node.scrollHeight;
        }
    }, [renderLogLine]);

    const clearTerminal = useCallback(() => {
        const container = terminalRef.current;
        if (container) {
            container.innerHTML = '';
            logCountRef.current = 0;
        }
        logCacheRef.current = [];
    }, []);

    // Load local databases list and sync settings from context
    useEffect(() => {
        if (!isLoggedIn) return;
        appSettingsApi.listLocalDbs().then(setLocalDatabases).catch(() => {});
    }, [isLoggedIn]);

    // Sync from context settings when they load
    useEffect(() => {
        if (!settings || Object.keys(settings).length === 0) return;
        if (typeof settings.galor_source_db === 'string') {
            setGalorSourceDb(settings.galor_source_db);
        }
        if (typeof settings.redshift_import_target_db === 'string') {
            setRedshiftTargetDb(settings.redshift_import_target_db);
        }
        if (settings.paging_config && typeof settings.paging_config === 'object') {
            setPagingConfig(settings.paging_config as PagingConfig);
        }
    }, [settings]);

    const toggleTable = useCallback((name: string) => {
        setTableConfigs((prev) =>
            prev.map((t) => (t.name === name ? { ...t, enabled: !t.enabled } : t))
        );
    }, []);

    const toggleAllTables = useCallback((checked: boolean) => {
        setTableConfigs((prev) => prev.map((t) => ({ ...t, enabled: checked })));
    }, []);

    const setImportRule = useCallback((name: string, rule: 'override' | 'add' | 'update' | 'incremental') => {
        setTableConfigs((prev) =>
            prev.map((t) => (t.name === name ? { ...t, importRule: rule } : t))
        );
    }, []);

    const setDaysBack = useCallback((name: string, days: number | undefined) => {
        setTableConfigs((prev) =>
            prev.map((t) => (t.name === name ? { ...t, daysBack: days } : t))
        );
    }, []);

    const setAllImportRules = useCallback((rule: 'override' | 'add' | 'update' | 'incremental') => {
        setTableConfigs((prev) => prev.map((t) => ({ ...t, importRule: rule })));
    }, []);

    const handleSetGalorSourceDb = useCallback((db: string) => {
        setGalorSourceDb(db);
        updateSetting('galor_source_db', db).catch(() => {});
    }, [updateSetting]);

    const handleSetRedshiftTargetDb = useCallback((db: string) => {
        setRedshiftTargetDb(db);
        updateSetting('redshift_import_target_db', db).catch(() => {});
    }, [updateSetting]);

    const updatePagingPageSize = useCallback((pageKey: string, pageSize: number) => {
        setPagingConfig((prev) => {
            if (!prev) return prev;
            return { ...prev, [pageKey]: { ...prev[pageKey], pageSize } };
        });
        setPagingDirty(true);
    }, []);

    const savePagingConfig = useCallback(() => {
        if (pagingConfig) {
            updateSetting('paging_config', pagingConfig).then(() => {
                setPagingDirty(false);
            }).catch(() => {});
        }
    }, [pagingConfig, updateSetting]);

    // --- SSE connection (shared for all job types) ---
    const connectSSE = useCallback(
        (jId: string, streamUrlFn?: (id: string) => string) => {
            const getUrl = streamUrlFn || dataSyncApi.getStreamUrl;
            const streamUrl = getUrl(jId);
            const es = new EventSource(streamUrl);
            eventSourceRef.current = es;

            es.addEventListener('log', (e: MessageEvent) => {
                const data = JSON.parse(e.data) as LogLine;
                appendLog(data);
            });

            es.addEventListener('table-status', (e: MessageEvent) => {
                const data = JSON.parse(e.data) as TableStatus;
                setTableStatuses((prev) => ({ ...prev, [data.name]: data }));
            });

            es.addEventListener('job-progress', (e: MessageEvent) => {
                const data = JSON.parse(e.data) as JobProgress;
                setJobProgress(data);
            });

            es.addEventListener('done', (e: MessageEvent) => {
                const data = JSON.parse(e.data);
                setJobStatus(data.success ? 'done' : 'failed');
                setJobProgress((prev) => prev ? { ...prev, percent: 100 } : null);
                setJobId(null);
                jobIdRef.current = null;
                es.close();
                eventSourceRef.current = null;
            });

            es.addEventListener('error', () => {
                setJobStatus('failed');
                setJobId(null);
                jobIdRef.current = null;
                es.close();
                eventSourceRef.current = null;
            });
        },
        [appendLog]
    );

    // --- S3 Snapshot actions ---
    const loadSnapshots = useCallback(async () => {
        setSnapshotsLoading(true);
        try {
            const result = await dataSyncApi.getSnapshots();
            setSnapshots(result);
        } catch {
            setSnapshots([]);
        }
        setSnapshotsLoading(false);
    }, []);

    // Load snapshots on mount
    useEffect(() => {
        if (!isLoggedIn) return;
        loadSnapshots();
    }, [isLoggedIn, loadSnapshots]);

    const startExport = useCallback(async () => {
        setJobStatus('running');
        setJobProgress(null);
        clearTerminal();
        try {
            const result = await dataSyncApi.startExport(redshiftTargetDb);
            setJobId(result.jobId);
            jobIdRef.current = result.jobId;
            connectSSE(result.jobId);
        } catch (err) {
            setJobStatus('failed');
            appendLog({
                timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
                text: `Failed to start export: ${err}`,
                type: 'error'
            });
        }
    }, [connectSSE, clearTerminal, appendLog, redshiftTargetDb]);

    const startS3Import = useCallback(async (snapshot: string, targetDb: string) => {
        setJobStatus('running');
        setJobProgress(null);
        clearTerminal();
        try {
            const result = await dataSyncApi.startS3Import(snapshot, targetDb);
            setJobId(result.jobId);
            jobIdRef.current = result.jobId;
            connectSSE(result.jobId);
        } catch (err) {
            setJobStatus('failed');
            appendLog({
                timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
                text: `Failed to start import: ${err}`,
                type: 'error'
            });
        }
    }, [connectSSE, clearTerminal, appendLog]);

    const deleteSnapshotAction = useCallback(async (snapshot: string) => {
        try {
            await dataSyncApi.deleteSnapshot(snapshot);
            setSnapshots((prev) => prev.filter((s) => s.key !== snapshot));
        } catch {
            // ignore
        }
    }, []);

    // --- Galor→Taytan sync ---
    const startSync = useCallback(async () => {
        setJobStatus('running');
        setJobProgress(null);
        setTableStatuses({});
        clearTerminal();

        const pending: Record<string, TableStatus> = {};
        tableConfigs.filter((t) => t.enabled).forEach((t) => {
            pending[t.name] = { name: t.name, status: 'pending' };
        });
        setTableStatuses(pending);

        try {
            const result = await dataSyncApi.startSync({
                tables: tableConfigs,
                targetDb,
                limit: limit ? parseInt(limit, 10) : undefined,
                importFromRedshift,
                redshiftMode,
                redshiftTargetDb: importFromRedshift ? redshiftTargetDb : undefined,
                galorSourceDb
            });

            setJobId(result.jobId);
            jobIdRef.current = result.jobId;
            connectSSE(result.jobId);
        } catch (err) {
            setJobStatus('failed');
            appendLog({
                timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
                text: `Failed to start sync: ${err}`,
                type: 'error'
            });
        }
    }, [tableConfigs, targetDb, limit, importFromRedshift, redshiftMode, redshiftTargetDb, galorSourceDb, connectSSE, clearTerminal, appendLog]);

    const stopSync = useCallback(async () => {
        const currentJobId = jobIdRef.current;
        if (currentJobId) {
            try {
                await dataSyncApi.stopJob(currentJobId);
            } catch {
                // ignore
            }
        }
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setJobStatus('idle');
        setJobId(null);
        jobIdRef.current = null;
        setTableStatuses((prev) => {
            const updated: Record<string, TableStatus> = {};
            for (const [name, status] of Object.entries(prev)) {
                updated[name] = status.status === 'running'
                    ? { ...status, status: 'failed', error: 'Stopped' }
                    : status;
            }
            return updated;
        });
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    return {
        // Table config
        tableConfigs,
        toggleTable,
        toggleAllTables,
        setImportRule,
        setDaysBack,
        setAllImportRules,

        // Settings
        targetDb,
        setTargetDb,
        limit,
        setLimit,
        importFromRedshift,
        setImportFromRedshift,
        redshiftMode,
        setRedshiftMode,

        // Galor source DB
        galorSourceDb,
        setGalorSourceDb: handleSetGalorSourceDb,
        redshiftTargetDb,
        setRedshiftTargetDb: handleSetRedshiftTargetDb,
        localDatabases,

        // Paging config
        pagingConfig,
        pagingDirty,
        updatePagingPageSize,
        savePagingConfig,

        // S3 Snapshots
        snapshots,
        snapshotsLoading,
        loadSnapshots,
        startExport,
        startS3Import,
        deleteSnapshot: deleteSnapshotAction,

        // Job state
        jobId,
        jobStatus,
        jobProgress,
        tableStatuses,

        // Terminal (DOM-based, no React state)
        terminalRef: setTerminalRef,
        clearTerminal,

        // Actions
        startSync,
        stopSync,

        // Used by the Automated tab to stream a scheduled run into this same terminal, rather than
        // standing up a second console for it.
        connectSSE,
        setJobStatus
    };
}
